import { useState } from "react";
import { Phone, Envelope, MapPin, Clock, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    sale_type: "",
    timeline: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const saleTypes = [
    "Estate Sale (after death)",
    "Living Estate Sale (downsizing)",
    "Moving Sale",
    "Liquidation",
    "Not sure - need guidance"
  ];

  const timelines = [
    "As soon as possible",
    "Within 2 weeks",
    "Within 1 month",
    "1-3 months",
    "Just exploring options"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build mailto link with form data
    const subject = encodeURIComponent(`Estate Sale Inquiry from ${formData.name} - ${formData.city}`);
    const body = encodeURIComponent(`
Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}
City: ${formData.city}
Type of Sale: ${formData.sale_type}
Timeline: ${formData.timeline}

Message:
${formData.message}
    `);
    
    // Open default email client
    window.location.href = `mailto:kathy@FargoEstateSales.com?subject=${subject}&body=${body}`;
    
    // Show success message
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 md:px-12 lg:px-24 py-24 bg-[#F7F5F0]" data-testid="contact-success">
        <div className="max-w-lg text-center">
          <div className="w-20 h-20 bg-[#2C4C3B] rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle weight="fill" className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-medium text-[#1C1914] mb-4">
            Thank You for Reaching Out
          </h1>
          <p className="text-lg text-[#5C564D] mb-8">
            We've received your message and will be in touch within 24 hours. If you need immediate assistance, please call us directly.
          </p>
          <a
            href="tel:2182982420"
            className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
            data-testid="success-phone-button"
          >
            <Phone weight="fill" className="w-5 h-5" />
            Call 218-298-2420
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Column - Contact Info */}
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                Contact Legacy Liquidators LLC - Fargo Estate Sales
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
                Schedule Your Free Estate Sale Consultation
              </h1>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                Ready to learn more about our estate sale services in Fargo, Moorhead, or the surrounding area? We're here to help families with estate liquidation, downsizing, and in-home estate sales. No need to clean first—just reach out and we'll guide you through the process.
              </p>

              {/* Click to Call */}
              <a
                href="tel:2182982420"
                className="flex items-center gap-4 bg-[#2C4C3B] hover:bg-[#1E3629] text-white p-6 rounded-lg mb-8 transition-all duration-300 group"
                data-testid="contact-phone-button"
              >
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <Phone weight="fill" className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white/80 text-sm mb-1">Call us directly</p>
                  <p className="text-2xl font-semibold">218-298-2420</p>
                </div>
              </a>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Envelope weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C564D] mb-1">Email</p>
                    <a href="mailto:kathy@FargoEstateSales.com" className="text-[#1C1914] font-medium hover:text-[#2C4C3B] transition-colors">
                      kathy@FargoEstateSales.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C564D] mb-1">Estate Sale Service Area</p>
                    <p className="text-[#1C1914] font-medium">
                      Fargo ND, Moorhead MN, West Fargo ND<br />Detroit Lakes MN, Fergus Falls MN & 60 miles
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C564D] mb-1">Response Time</p>
                    <p className="text-[#1C1914] font-medium">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white border border-[#E2DCD0] p-8 md:p-10 rounded-lg">
              <h2 className="text-2xl font-serif font-medium text-[#1C1914] mb-2">
                Request a Free Consultation
              </h2>
              <p className="text-[#5C564D] mb-8">
                Fill out the form below and we'll be in touch to discuss your needs.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#1C1914] mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                      placeholder="John Smith"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#1C1914] mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                      placeholder="(218) 555-0123"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#1C1914] mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-[#1C1914] mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                      placeholder="Fargo, ND"
                      data-testid="input-city"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="sale_type" className="block text-sm font-medium text-[#1C1914] mb-2">
                    Type of Sale Needed *
                  </label>
                  <select
                    id="sale_type"
                    name="sale_type"
                    required
                    value={formData.sale_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                    data-testid="select-sale-type"
                  >
                    <option value="">Select a type...</option>
                    {saleTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium text-[#1C1914] mb-2">
                    Timeline *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    required
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914]"
                    data-testid="select-timeline"
                  >
                    <option value="">Select a timeline...</option>
                    {timelines.map((timeline, index) => (
                      <option key={index} value={timeline}>{timeline}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#1C1914] mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E2DCD0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:border-transparent bg-white text-[#1C1914] resize-none"
                    placeholder="Tell us a bit about your situation..."
                    data-testid="textarea-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] disabled:bg-[#2C4C3B]/50 text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <PaperPlaneTilt weight="fill" className="w-5 h-5" />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Snippet */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-8 text-center">
            Common Questions When Contacting Us
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F7F5F0] p-6 rounded-lg">
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2">Do I need to clean first?</h3>
              <p className="text-[#5C564D] text-sm">No. You don't need to sort, organize, or clean anything before contacting us. We handle homes exactly as they are.</p>
            </div>
            <div className="bg-[#F7F5F0] p-6 rounded-lg">
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2">What happens during a consultation?</h3>
              <p className="text-[#5C564D] text-sm">We'll visit your home, assess the contents, discuss your timeline and goals, and explain our process and fees.</p>
            </div>
            <div className="bg-[#F7F5F0] p-6 rounded-lg">
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2">Is there a fee for the consultation?</h3>
              <p className="text-[#5C564D] text-sm">No, our initial consultation is completely free with no obligation.</p>
            </div>
            <div className="bg-[#F7F5F0] p-6 rounded-lg">
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2">How quickly can you schedule a sale?</h3>
              <p className="text-[#5C564D] text-sm">Typically 2-4 weeks from consultation to sale. We can sometimes accommodate urgent timelines.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
